import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Badge, Box, CircularProgress, Divider, IconButton,
  List, ListItem, ListItemText, Popover, Tooltip, Typography, Button,
} from '@mui/material'
import {
  Notifications as BellIcon,
  NotificationsNone as BellEmptyIcon,
  TrendingUp as DealIcon,
  CheckCircleOutline as ReadIcon,
  Alarm as AlarmIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router'
import { notificationsApi, AppNotification } from '../../lib/api/notificationsApi'
import { themeColors } from '../../app/layout/theme'
import { requestFirebaseToken, registerOnMessage } from '../../firebase'

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins < 1)    return 'just now'
  if (mins < 60)   return `${mins}m ago`
  if (hours < 24)  return `${hours}h ago`
  if (days === 1)  return 'yesterday'
  return `${days}d ago`
}

// ── Entity → route ────────────────────────────────────────────────────────────

function entityRoute(n: AppNotification) {
  if (!n.entityId) return null
  if (n.entityType === 'DEAL')    return `/deals/${n.entityId}`
  if (n.entityType === 'LEAD')    return `/leads/${n.entityId}`
  if (n.entityType === 'CONTACT') return `/contacts/${n.entityId}`
  return null
}

function formatBody(body: string | null | undefined): string {
  if (!body) return ''
  return body.replace(/\[FID:[^\]]+\]/g, '').trim()
}

// ── Icon per notification type ────────────────────────────────────────────────

function NotifIcon({ type }: { type: string }) {
  if (type.startsWith('DEAL')) {
    return <DealIcon sx={{ fontSize: 16, color: themeColors.primary }} />
  }
  if (type.includes('REMINDER') || type.includes('FOLLOW_UP')) {
    return <AlarmIcon sx={{ fontSize: 16, color: '#ef4444' }} />
  }
  return <BellIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
}

// ── Synthesized chime ────────────────────────────────────────────────────────
function playChime() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(587.33, now); // D5
    osc.frequency.setValueAtTime(880.00, now + 0.12); // A5
    
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start(now);
    osc.stop(now + 0.4);
  } catch (e) {
    // ignore
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const open = Boolean(anchor)

  // Request Notification permission and initialize Firebase Messaging on mount
  useEffect(() => {
    // 1. Initialize Firebase FCM token request
    requestFirebaseToken().then((token) => {
      // FCM registered for this session
    });

    // 2. Set up foreground push listener with cleanups
    const unsubscribe = registerOnMessage((payload: any) => {
      // Play premium chime
      playChime();

      // Invalidate queries so that bell badge and list update immediately
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notification-count'] });
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] });
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] });

      // Display system/browser notification popup
      if ('Notification' in window && Notification.permission === 'granted') {
        const title = payload.notification?.title || 'CRM Event Reminder';
        const body = payload.notification?.body || 'You have an upcoming event scheduled.';
        new Notification(title, {
          body,
          icon: '/favicon.ico'
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [qc]);

  // Poll for latest unread notifications to trigger browser notifications
  const { data: unreadNotifications = [] } = useQuery({
    queryKey: ['unread-notifications-poll'],
    queryFn:  () => notificationsApi.getAll(true, 5), // get only unread
    refetchInterval: 10_000, // check every 10 seconds for instant response!
  })

  const isFirstLoad = useRef(true)
  const processedNotifs = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (unreadNotifications.length === 0) {
      isFirstLoad.current = false
      return
    }

    if (isFirstLoad.current) {
      // On first load, just populate the processed set so we don't display old unread notifications
      unreadNotifications.forEach(n => processedNotifs.current.add(n.id))
      isFirstLoad.current = false
      return
    }

    unreadNotifications.forEach((n) => {
      if (!processedNotifs.current.has(n.id)) {
        processedNotifs.current.add(n.id)

        // Play premium synthesizer notification chime
        playChime()

        // Show browser desktop/laptop notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(n.title, {
            body: formatBody(n.body) || 'You have a new notification',
            icon: '/favicon.ico'
          })
        }
      }
    })
  }, [unreadNotifications])

  const { data: count = 0 } = useQuery({
    queryKey: ['notification-count'],
    queryFn:  notificationsApi.getUnreadCount,
    refetchInterval: 30_000,
  })

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn:  () => notificationsApi.getAll(false, 30),
    enabled:  open,
  })

  const markReadMut = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notification-count'] })
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] })
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] })
    },
  })

  const markAllMut = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notification-count'] })
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] })
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] })
    },
  })

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)
  const handleClose = () => setAnchor(null)

  const handleClick = (n: AppNotification) => {
    if (!n.isRead) markReadMut.mutate(n.id)
    const route = entityRoute(n)
    if (route) { navigate(route); handleClose() }
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton size="small" onClick={handleOpen} sx={{ color: '#6b7280' }}>
          <Badge
            badgeContent={count > 0 ? count : undefined}
            color="error"
            max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: 16, height: 16, p: '0 4px' } }}
          >
            {count > 0
              ? <BellIcon sx={{ fontSize: 20 }} />
              : <BellEmptyIcon sx={{ fontSize: 20 }} />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 360,
            borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb',
            mt: 0.5,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700} fontSize="15px">Notifications</Typography>
          {count > 0 && (
            <Button
              size="small"
              onClick={() => markAllMut.mutate()}
              disabled={markAllMut.isPending}
              startIcon={<ReadIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontSize: '12px', color: themeColors.primary, p: 0, minWidth: 0 }}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* Body */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <BellEmptyIcon sx={{ fontSize: 36, color: '#d1d5db', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No notifications yet</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {notifications.map((n, i) => (
              <Box key={n.id}>
                {i > 0 && <Divider sx={{ borderColor: '#f3f4f6' }} />}
                <ListItem
                  onClick={() => handleClick(n)}
                  sx={{
                    px: 2.5, py: 1.5,
                    cursor: entityRoute(n) ? 'pointer' : 'default',
                    bgcolor: n.isRead ? 'transparent' : `${themeColors.primary}05`,
                    '&:hover': { bgcolor: '#f9fafb' },
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  {/* Left: icon dot */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: '2px' }}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '8px',
                      bgcolor: `${themeColors.primary}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <NotifIcon type={n.type} />
                    </Box>
                    {!n.isRead && (
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: themeColors.primary }} />
                    )}
                  </Box>

                  {/* Right: text */}
                  <ListItemText
                    primary={
                      <Typography fontSize="13px" fontWeight={n.isRead ? 500 : 700} color="text.primary">
                        {n.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {n.body && (
                          <Typography fontSize="12px" color="text.secondary" sx={{ mb: 0.25 }} noWrap>
                            {formatBody(n.body)}
                          </Typography>
                        )}
                        <Typography fontSize="11px" color="text.disabled">
                          {relativeTime(n.createdAt)}
                        </Typography>
                      </Box>
                    }
                    disableTypography
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center', bgcolor: '#fafafa' }}>
          <Button
            fullWidth
            size="small"
            onClick={() => {
              setAnchor(null)
              navigate('/notifications')
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '12px',
              color: themeColors.primary,
              py: 0.75,
              borderRadius: '8px',
              '&:hover': {
                bgcolor: `${themeColors.primary}08`
              }
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  )
}
